// React Components
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// Material UI Components
import { Grid, Paper } from "@mui/material";
import Link from "@mui/material/Link";
import Button from "@mui/material/Button";
import { Alert, AlertTitle } from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

// Audit Vault Components
import AccessDenied from "../components/common/AccessDenied";
import ServiceIsDown from "../components/common/ServiceIsDown";
import ProgressBar from "../components/common/ProgressBar";
import ImageExchange from "../imgs/iconExchange.svg";
import AuditGrowth from "../components/reports/AuditGrowth";
import PeriodDropDown from "../components/reports/PeriodDropDown";
import ExchangeDeletedItems from "../components/reports/ExchangeDeletedItems";
import TenantSetupRequired from "../components/tenant/TenantSetupRequired";
import InsightsSearchBox from "../components/reports/InsightsSearchBox";

// Audit Vault Utilities
import { getCompanyByCompanyId } from "../utilities/common-company";
import { NumberBadge } from "../utilities/common-ui-utils";
import { formatAuditJobRunTimeDateServerTimeToGMT } from "../utilities/common-date-utils";
import CustomerPageScaffold from "../components/common/CustomerPageScaffold";
import FilterPanel from "../components/common/FilterPanel";
import { verifyUserHasTenantRolePermissions } from "../utilities/common-user-utils";
import { ROLE_CODE_TENANTREPORTREADER } from "../constants/constants-roles";
import {
    REPORT_PERIOD_LIST,
    REPORT_ON_PERIOD_30DAYS,
    REPORT_ON_PERIOD_90DAYS,
    REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE,
} from "../constants/constants-reportperiods";
import { getHealthStatsByTenantId } from "../utilities/common-healthstats";

const theBreadcrumbPath = [
    { name: "Home", link: "/" },
    { name: "Reports", link: "/Reports/ReportsHome" },
    { name: "Exchange Reports", link: "/Reports/Exchange" },
];

const theSectionTitle = "Exchange Reports Dashboard";
const theSectionDesc =
    "Exchange audits capture crucial metadata for mailbox activity, administration, mail flow rules, exchange admin and compliance events.  Note: Audit info regarding reading and sending emails are not logged as part of the Exchange admin audit logs.  The reports below allow you to obtain insights into the aforementioned audit information.  Below is a visual representation of your essential Exchange audit data.";
const isAdminPage = false;

// Common styles for report links inside tab panels (same as SharePoint)
const linkListSx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
};

const reportLinkSx = {
    display: "block",
    width: "100%",
    textAlign: "left",
    whiteSpace: "normal",
    marginBottom: 0.5,
};

/*
Displays the Exchange Reports home page for Company X.

Future:
- Activity:
    New mailboxes (New-Mailbox) in last 30 days, last 3 months
    ?Create (new Note or Calendar entries) in last 30 days, last 3 months
- User: Deleted items by User
*/
function ReportsExchange(props) {
    const { user, openCompanyNag, setOpenCompanyNag } = props;

    // Component Constants
    const [company, setCompany] = useState("");
    const [tenantHealthStats, setTenantHealthStats] = useState("");
    const [focusPeriodId, setFocusPeriodId] = useState(REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE);
    const [accessDenied, setAccessDenied] = useState(false);

    const [loading, setLoading] = useState(true);
    const [serviceIsDownError, setServiceIsDownError] = useState(false);
    const navigate = useNavigate();

    const [totalExchangeAuditRecords, setTotalExchangeAuditRecords] = useState(null);
    const [loadingTotalExchangeAuditRecords, setLoadingTotalExchangeAuditRecords] = useState(false);
    const [totalExchangeAuditRecordsLoaded, setTotalExchangeAuditRecordsLoaded] = useState(false);

    const [selectedTenantOption, setSelectedTenantOption] = useState("");
    const [selectedPeriodOption, setSelectedPeriodOption] = useState("");
    const [tabValue, setTabValue] = useState(0);

    // Component Functions
    // Load TenantHealthStats given the tenandId.
    async function fetchTenantHealthStats(theTenantId) {
        setLoading(true);
        try {
            const response2 = await getHealthStatsByTenantId(theTenantId, 3, user);
            if (response2) {
                setTenantHealthStats(response2);
            }
            setLoading(false);
        } catch (e) {
            console.log("ERROR: ReportExchange.fetchTenantHealthStats");
            console.log(e);
            setServiceIsDownError(true);
        } finally {
            setLoading(false);
        }
    }

    // Load the total Exchange audit records on demand.
    async function fetchTotalAuditRecordsOnDemand() {
        if (!user?.tenantId || loadingTotalExchangeAuditRecords || totalExchangeAuditRecordsLoaded) {
            return;
        }

        setLoadingTotalExchangeAuditRecords(true);

        try {
            // Heavier on-demand total counts load.
            const response = await getHealthStatsByTenantId(user.tenantId, 13, user);            
            if (response) {
                const exchangeTotal = response.totalExchangeAuditRecords;
                setTotalExchangeAuditRecords(exchangeTotal);
                setTotalExchangeAuditRecordsLoaded(true);                
            }
        }
        catch (e) {
            console.log("ERROR: Report.fetchTotalAuditRecordsOnDemand");
            console.log(e);
            setServiceIsDownError(true);
        }
        finally {
            setLoadingTotalExchangeAuditRecords(false);
        }
    }


    useEffect(() => {
        async function fetchCompanyData() {
            try {

                if (!verifyUserHasTenantRolePermissions(user, ROLE_CODE_TENANTREPORTREADER)) {
                    setAccessDenied(true);
                    return;
                }
                if (user && user.companyId) {
                    const response1 = await getCompanyByCompanyId(
                        user.companyId,
                        setServiceIsDownError
                    );
                    if (response1) {
                        setCompany(response1);

                        if (response1.tenantList && response1.tenantList.length > 0) {
                            fetchTenantHealthStats(response1.tenantList[0].id);
                            setSelectedTenantOption(response1.tenantList[0]); // Set to the first item in the list.
                            // Set the default period in the drop down list (we default to 30 days).
                            setSelectedPeriodOption(
                                REPORT_PERIOD_LIST[REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE]
                            );
                        }
                    }
                }

                setLoading(false);
            } catch (e) {
                console.log("ERROR: ReportExchange.fetchCompanyData");
                console.log(e);
                setServiceIsDownError(true);
            } finally {
                setLoading(false);
            }
        }

        fetchCompanyData();
    }, []);

    // Tab panel.
    function TabPanel(props) {
        const { children, value, index, ...other } = props;

        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <Box sx={{ p: 3 }}>
                        <Box sx={linkListSx}>{children}</Box>
                    </Box>
                )}
            </div>
        );
    }

    TabPanel.propTypes = {
        children: PropTypes.node,
        value: PropTypes.number.isRequired,
        index: PropTypes.number.isRequired,
    };

    function a11yProps(index) {
        return {
            id: `simple-tab-${index}`,
            "aria-controls": `simple-tabpanel-${index}`,
        };
    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Event handlers for drop down lists.
    const handleTenantOptionChange = (event) => {
        setSelectedTenantOption(event.target.value);
        fetchTenantHealthStats(event.target.value.id);
    };
    const handlePeriodOptionChange = (event) => {
        setSelectedPeriodOption(event.target.value);
        setFocusPeriodId(event.target.value.id);
    };

    // Component UI
    if (loading) {
        console.log("Loading Data ....");
        return <ProgressBar message="Loading ..." loading={loading} />;
    }

    if (serviceIsDownError) {
        return (
            <>
                <ServiceIsDown />
            </>
        );
    }

    if (!accessDenied && selectedTenantOption && !selectedTenantOption.logExchangeAudit) {
        return (
            <Alert severity="info">
                <AlertTitle>Exchange Audit Logging is not enabled for this tenant.</AlertTitle>
                If you would like this feature, please enable Preserve Exchange Audit Logs by editing
                your Tenant settings (or{" "}
                <Link onClick={() => navigate(`/Support/SupportHome`)} component="button">
                    Contact your Administrator
                </Link>
                ).
            </Alert>
        );
    }

    // Get the total audit record count.
    let totalAuditRecords = 0;
    if (tenantHealthStats.totalExchangeAuditRecords) {
        totalAuditRecords = tenantHealthStats.totalExchangeAuditRecords;
    }

    // A valid Tenant is chosen from the textfield selection drop down list.
    return (
        <>
            <CustomerPageScaffold
                breadcrumbPaths={theBreadcrumbPath}
                sectionTitle={theSectionTitle}
                sectionDescription={theSectionDesc}
                isAdminPage={isAdminPage}
                company={company}
                selectedTenant={selectedTenantOption}
                openCompanyNag={openCompanyNag}
                setOpenCompanyNag={setOpenCompanyNag}
                headerAdornment={<Box component="img" src={ImageExchange} alt="Exchange Reports" sx={{ width: 75, height: 'auto' }} />}
            />


            <Box>
                <Grid container spacing={2}>
                    {accessDenied && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <AccessDenied />
                        </Grid>
                    )}

                    {!selectedTenantOption && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <Alert severity="error">
                                <AlertTitle>
                                    Your Company has not yet completed the setup process or you have not yet
                                    specified a valid Tenant.
                                </AlertTitle>
                            </Alert>
                        </Grid>
                    )}

                    {tenantHealthStats.status === 0 && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <TenantSetupRequired />
                        </Grid>
                    )}

                    {!accessDenied &&
                        selectedTenantOption &&
                        tenantHealthStats.status >= 1 ? (
                        <>
                            <Grid item xs={12}>
                                <FilterPanel title="Dashboard filters">
                                    <PeriodDropDown
                                        selectedPeriodOption={selectedPeriodOption}
                                        handlePeriodOptionChange={handlePeriodOptionChange}
                                    />
                                </FilterPanel>
                            </Grid>

                            {/* Last audit job run time */}
                            <Grid item xs={12} sx={{ p: 2 }}>
                                Audit data last updated on:{" "}
                                {formatAuditJobRunTimeDateServerTimeToGMT(
                                    tenantHealthStats.exchangeAuditLastSuccessfulRunTime
                                )}
                                <br />
                            </Grid>


                            {/* AuditGrowth, Deleted Items (operation), Deleted Items (user) */}
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{ p: 2, border: "1px solid lightgray", height: "100%" }}
                                >
                                    <div style={{ height: "100%" }}>
                                        <AuditGrowth
                                            tenantId={user.tenantId}
                                            periodToUse={focusPeriodId}
                                            reportType="Exchange"
                                        />
                                    </div>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{ p: 2, border: "1px solid lightgray", height: "100%" }}
                                >
                                    <ExchangeDeletedItems
                                        user={user}
                                        companyId={company.id}
                                        tenantId={user.tenantId}
                                        periodToUse={focusPeriodId}
                                        loadData={false}
                                        filterByColumn={"Operation"}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{ p: 2, border: "1px solid lightgray", height: "100%" }}
                                >
                                    <ExchangeDeletedItems
                                        user={user}
                                        companyId={company.id}
                                        tenantId={user.tenantId}
                                        periodToUse={focusPeriodId}
                                        loadData={false}
                                        filterByColumn={"userid"}
                                    />
                                </Paper>
                            </Grid>

                            { /* Display total Exchange audit record count on demand (lazy load).  Optimizes performance to show only on demand.  */}
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                    <Box sx={{ height: '100%' }}>
                                        <Typography
                                            component="div"
                                            variant="h6"
                                            sx={{ fontSize: 16, pb: 2 }}
                                        >
                                            Total Audit Records
                                        </Typography>

                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                            {!totalExchangeAuditRecordsLoaded && !loadingTotalExchangeAuditRecords && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={fetchTotalAuditRecordsOnDemand}
                                                >
                                                    Show total records
                                                </Button>
                                            )}

                                            {loadingTotalExchangeAuditRecords && (
                                                <Typography
                                                    component="div"
                                                    variant="body2"
                                                    sx={{ fontSize: 15 }}
                                                >
                                                    Loading count...
                                                </Typography>
                                            )}

                                            {totalExchangeAuditRecordsLoaded && !loadingTotalExchangeAuditRecords && (
                                                <TableContainer sx={{ mt: 1 }}>
                                                    <Table style={{ width: '100%', fontSize: 'inherit', fontWeight: 'inherit' }}>
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell style={{ border: 'none', padding: 2 }}>
                                                                    Exchange
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    style={{ border: 'none', padding: 2 }}
                                                                >
                                                                    <NumberBadge
                                                                        number={totalExchangeAuditRecords?.toLocaleString()}
                                                                        bgcolor="#36a2eb"
                                                                        txtcolor="white"
                                                                    />                          
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            <InsightsSearchBox colSpanVal={8} workloadName={"Exchange"} />

                            {/* Tabs section */}
                            <Grid item xs={12} sx={{ p: 2 }}>
                                <Box sx={{ width: "100%" }}>
                                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                        <Tabs
                                            value={tabValue}
                                            onChange={handleTabChange}
                                            aria-label="Exchange Reports Tab"
                                        >
                                            <Tab label="Activity Reports" {...a11yProps(0)} />
                                            <Tab label="User Reports" {...a11yProps(1)} />
                                            <Tab label="Top Activity" {...a11yProps(2)} />
                                        </Tabs>
                                    </Box>

                                    <TabPanel value={tabValue} index={0}>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeDeletedItemsReport/${REPORT_ON_PERIOD_30DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Deleted Items (Last 30 days)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeDeletedItemsReport/${REPORT_ON_PERIOD_90DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Deleted Items (Last 3 months)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeDeletedItemsReport/${REPORT_ON_PERIOD_30DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Deleted Items by User (Last 30 days)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeDeletedItemsReport/${REPORT_ON_PERIOD_90DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Deleted Items by User (Last 3 months)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeMailboxesCreatedReport/${REPORT_ON_PERIOD_30DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Mailboxes Created (Last 30 days)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeMailboxesCreatedReport/${REPORT_ON_PERIOD_90DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Mailboxes Created (Last 3 months)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeMailboxesCreatedReport/${REPORT_ON_PERIOD_30DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Mailboxes Created by User (Last 30 days)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeMailboxesCreatedReport/${REPORT_ON_PERIOD_90DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Mailboxes Created by User (Last 3 months)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeMailboxesDeletedReport/${REPORT_ON_PERIOD_30DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Mailboxes Deleted (Last 30 days)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeMailboxesDeletedReport/${REPORT_ON_PERIOD_90DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Mailboxes Deleted (Last 3 months)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeMailboxesDeletedReport/${REPORT_ON_PERIOD_30DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Mailboxes Deleted by User (Last 30 days)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeMailboxesDeletedReport/${REPORT_ON_PERIOD_90DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Mailboxes Deleted by User (Last 3 months)
                                        </Link>
                                    </TabPanel>

                                    <TabPanel value={tabValue} index={1}>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeAllActivityByUserReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            All Activity by User
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeMailboxesCreatedOrDeletedByUserReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Mailboxes Created or Deleted by Modifying User
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ExchangeAllActivityByIPAddressReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            All Activity by IP Address
                                        </Link>
                                    </TabPanel>
                                    <TabPanel value={tabValue} index={2}>
                                        <Link sx={reportLinkSx} onClick={() => navigate('/Reports/TopActivity/exchange/users')} component="button">Top Users by Activity</Link>
                                        <Link sx={reportLinkSx} onClick={() => navigate('/Reports/TopActivity/exchange/operations')} component="button">Top Operations by Activity</Link>
                                    </TabPanel>
                                </Box>
                            </Grid>
                        </>
                    ) : (
                        !accessDenied &&
                        selectedTenantOption &&
                        tenantHealthStats.status !== 0 && (
                            <Grid item xs={12} sx={{ p: 2 }}>
                                <ProgressBar message="Loading ..." loading={true} />
                            </Grid>
                        )
                    )}

                    <Grid item xs={12} sx={{ p: 2 }}>
                        <br />
                        Need a custom or new report? Provide us with your{" "}
                        <Link onClick={() => navigate(`/Support/SupportHome`)} component="button">
                            Product Feedback
                        </Link>
                        .
                        <br />
                        * Unless otherwise specified as UTC, graphs on page are using your browser's local
                        timezone.
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}

export default ReportsExchange;
